import { useEffect, useState } from 'react';
import type { CategoryRef, ProductListItem, ProductStats } from '@kassa/shared';
import { api, ApiError } from '../api';
import { withQuery } from '../api/client';
import { formatMoney, formatQty, UNIT_LABEL } from '../format';
import { DataTable, DownloadMenu, HeadButton, NewButton, PageHeader, Pager, SearchPanel, useUrlFilters, type Column } from '../components/ListPage';
import { VatCheckBadge } from '../components/VatCheckBadge';

interface Props {
  canEdit: boolean;
  canImport: boolean;
  onOpen: (id: string) => void;
  onNew: () => void;
  onImport: () => void;
}

const PAGE = 25;
const KEYS = ['q', 'cat', 'st', 'page', 'sort'] as const;

// Məhsul siyahısı (KT-99 şablonu): «+ Yeni məhsul» əsas düymədir; axtarış (ad, barkod, PLU, qısa kod), kateqoriya,
// status; süzgəc URL-də; Excel ixracı; boş kataloqda «İlk məhsulu əlavə et» və «Excel-dən yüklə».
export function Products({ canEdit, canImport, onOpen, onNew, onImport }: Props) {
  const [f, setF] = useUrlFilters(KEYS);
  const [draft, setDraft] = useState({ cat: f.cat, st: f.st });
  const [q, setQ] = useState(f.q);
  const [categories, setCategories] = useState<CategoryRef[]>([]);
  const [items, setItems] = useState<ProductListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const page = Number(f.page) || 1;
  const params = { q: f.q || undefined, categoryId: f.cat || undefined, includeInactive: f.st === 'all' ? '1' : undefined, sort: f.sort || undefined };
  // Göstərici kartları (KT-108, maket 26) — süzgəcdən asılı deyil, şirkət (üzvün obyektləri) üzrə
  const [stats, setStats] = useState<ProductStats | null>(null);
  useEffect(() => {
    api.productStats().then(setStats).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    api.productCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .products({ ...params, page, limit: PAGE })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [f.q, f.cat, f.st, f.sort, page]);

  const filtered = Boolean(f.q || f.cat || f.st);
  const columns: Column<ProductListItem>[] = [
    { key: 'bar', label: 'Barkod', mono: true, minor: true, render: (p) => p.barcodes[0] ?? '—' },
    {
      key: 'name',
      label: 'Ad',
      sortKey: 'name',
      render: (p) => (
        <button type="button" className="cell-link" onClick={() => onOpen(p.id)}>
          {p.name}
        </button>
      ),
    },
    { key: 'cat', label: 'Kateqoriya', minor: true, render: (p) => p.categoryName ?? '—' },
    { key: 'unit', label: 'Vahid', minor: true, render: (p) => UNIT_LABEL[p.unitCode] ?? p.unitCode },
    { key: 'price', label: 'Qiymət', align: 'right', mono: true, sortKey: 'price', render: (p) => formatMoney(p.retailPrice) },
    { key: 'qty', label: 'Qalıq', align: 'right', mono: true, sortKey: 'qty', render: (p) => formatQty(p.qtyPhys) },
    { key: 'st', label: 'Status', render: (p) => <span className={p.active ? 'badge badge-ok' : 'badge badge-off'}>{p.active ? 'Aktiv' : 'Passiv'}</span> },
    // ƏDV yoxlaması (KT-69): yalnız diqqət tələb edəndə (uyğunsuz, natamam kod, təsdiqlənmiş seçim)
    { key: 'vat', label: 'ƏDV', minor: true, render: (p) => (p.vatLevel === 'red' || p.vatLevel === 'yellow' || p.vatLevel === 'blue' ? <VatCheckBadge level={p.vatLevel} /> : '') },
  ];

  return (
    <main className="page page-list">
      <PageHeader
        title="Məhsullar"
        actions={
          <>
            {canImport && <HeadButton label="Excel-dən yüklə" onClick={onImport} />}
            {canEdit && <NewButton label="Yeni məhsul" onClick={onNew} />}
          </>
        }
      />

      {stats && (
        <div className="list-kpis" aria-label="Göstəricilər">
          <button type="button" className="list-kpi" onClick={() => setF({ st: 'all', page: '' })}>
            <span className="muted small">Cəmi mal</span>
            <strong>{stats.total}</strong>
          </button>
          <button type="button" className="list-kpi" onClick={() => setF({ st: '', page: '' })}>
            <span className="muted small">Aktiv</span>
            <strong>{stats.active}</strong>
          </button>
          <div className="list-kpi">
            <span className="muted small">Deaktiv</span>
            <strong>{stats.inactive}</strong>
          </div>
          <div className="list-kpi is-warn">
            <span className="muted small">Az qalan</span>
            <strong>{stats.low}</strong>
          </div>
          <div className="list-kpi is-bad">
            <span className="muted small">Qalığı yoxdur</span>
            <strong>{stats.zero}</strong>
          </div>
        </div>
      )}

      <div className="list-bar">
        <form
          className="list-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setF({ q: q.trim(), page: '' });
          }}
        >
          <input type="search" aria-label="Axtar" placeholder="Ad, barkod, PLU və ya qısa kod" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
        <DownloadMenu items={[{ label: 'Excel', url: withQuery('/api/products/export', params), name: 'mehsullar.xlsx' }]} />
      </div>

      <SearchPanel
        defaultOpen={Boolean(f.cat || f.st)}
        onSearch={() => setF({ cat: draft.cat, st: draft.st, page: '' })}
        onClear={() => {
          setDraft({ cat: '', st: '' });
          setQ('');
          setF({ q: '', cat: '', st: '', page: '' });
        }}
      >
        <label className="field">
          Kateqoriya
          <select value={draft.cat} onChange={(e) => setDraft({ ...draft, cat: e.target.value })}>
            <option value="">Hamısı</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Status
          <select value={draft.st} onChange={(e) => setDraft({ ...draft, st: e.target.value })}>
            <option value="">Aktiv</option>
            <option value="all">Hamısı</option>
          </select>
        </label>
      </SearchPanel>

      <DataTable
        label="Məhsullar"
        sort={f.sort || 'name'}
        onSort={(v) => setF({ sort: v === 'name' ? '' : v, page: '' })}
        columns={columns}
        rows={items}
        rowKey={(p) => p.id}
        onEdit={(p) => onOpen(p.id)}
        loading={loading}
        error={error}
        empty={
          filtered ? (
            <p className="muted">Bu süzgəclə məhsul tapılmadı.</p>
          ) : (
            <>
              <p className="muted">Kataloq boşdur.</p>
              <div className="table-empty-actions">
                {canEdit && (
                  <button type="button" className="btn-sec" onClick={onNew}>
                    İlk məhsulu əlavə et
                  </button>
                )}
                {canImport && (
                  <button type="button" className="btn-small" onClick={onImport}>
                    Excel-dən yüklə
                  </button>
                )}
              </div>
            </>
          )
        }
      />
      <Pager page={page} limit={PAGE} total={total} unit="məhsul" onPage={(p) => setF({ page: p > 1 ? String(p) : '' })} />
    </main>
  );
}
